// State management for the dashboard

import type { Session, NoProductEvent, Alternative, LiveSession, LiveEvent } from './types';

// Pagination state for various lists
interface PaginatedState<T> {
  items: T[];
  total: number;
  offset: number;
  limit: number;
}

interface FilterState {
  startDate: string | null;
  endDate: string | null;
  sortBy: string;
  sortOrder: 'ASC' | 'DESC';
}

// Sessions state
export const sessionsState: PaginatedState<Session> & FilterState = {
  items: [],
  total: 0,
  offset: 0,
  limit: 20,
  startDate: null,
  endDate: null,
  sortBy: 'started_at',
  sortOrder: 'DESC'
};

// No products state
export const noProductsState: PaginatedState<NoProductEvent> & FilterState & { searchQuery: string | null } = {
  items: [],
  total: 0,
  offset: 0,
  limit: 20,
  startDate: null,
  endDate: null,
  sortBy: 'created_at',
  sortOrder: 'DESC',
  searchQuery: null
};

// Alternatives state
export const alternativesState: PaginatedState<Alternative> & FilterState & {
  searchedProduct: string | null;
  suggestedProduct: string | null;
  category: string | null;
} = {
  items: [],
  total: 0,
  offset: 0,
  limit: 20,
  startDate: null,
  endDate: null,
  sortBy: 'created_at',
  sortOrder: 'DESC',
  searchedProduct: null,
  suggestedProduct: null,
  category: null
};

// Products state
export const productsState: PaginatedState<any> & FilterState & {
  category: string | null;
  inStock: boolean | null;
  search: string | null;
} = {
  items: [],
  total: 0,
  offset: 0,
  limit: 20,
  startDate: null,
  endDate: null,
  sortBy: 'total_mentions',
  sortOrder: 'DESC',
  category: null,
  inStock: null,
  search: null
};

// Categories state
export const categoriesState: PaginatedState<any> & FilterState = {
  items: [],
  total: 0,
  offset: 0,
  limit: 20,
  startDate: null,
  endDate: null,
  sortBy: 'product_count',
  sortOrder: 'DESC'
};

// Out of stock state
export const outOfStockState: PaginatedState<any> = {
  items: [],
  total: 0,
  offset: 0,
  limit: 20
};

// Orders state
export const ordersState: PaginatedState<any> & FilterState & {
  attribution: string | null;
  orderId: string | null;
} = {
  items: [],
  total: 0,
  offset: 0,
  limit: 20,
  startDate: null,
  endDate: null,
  sortBy: 'order_date',
  sortOrder: 'DESC',
  attribution: null,
  orderId: null
};

// Live view state
export const liveState = {
  sessions: new Map<string, LiveSession>(),
  events: [] as LiveEvent[],
  eventSource: null as EventSource | null,
  maxEvents: 1000, // Keep last 1000 events
  sessionTimeout: 5 * 60 * 1000, // 5 minutes
  idleTimeout: 2 * 60 * 1000, // 2 minutes
  reconnectAttempts: 0
};

// App state
export const appState = {
  currentTab: 'session',
  selectedClientId: '',  // Set from /api/config on load
  refreshInterval: null as ReturnType<typeof setInterval> | null,
  refreshRate: 30000, // 30 seconds
  // Session Intelligence filters (preserved across auto-refresh)
  sessionIntelligenceFilters: {
    startDate: null as string | null,
    endDate: null as string | null
  }
};

// Reset a paginated state
export function resetPaginatedState<T>(state: PaginatedState<T>): void {
  state.items = [];
  state.offset = 0;
  state.total = 0;
}

// Update items in paginated state
export function updatePaginatedState<T>(
  state: PaginatedState<T>,
  items: T[],
  total: number,
  reset: boolean = false
): void {
  if (reset) {
    state.items = items;
    state.offset = items.length;
  } else {
    state.items = [...state.items, ...items];
    state.offset += items.length;
  }
  state.total = total;
}
